import { useWorkspaceText } from '../interface-copy';
import { useCompany } from '@/company/context';
import { useNamespaceCopy } from '@/i18n/ns';
import workspaceFeaturesCopy from '@/i18n/ns/workspace-features';
import editorCopy from '@/i18n/ns/workspace-editors';
import { formatCopy } from '@/i18n/ns';
import { useWorkspace } from '../context';
import { useSkillLibrary } from '../retrieval';
import { WorkspaceMarkdown } from '../markdown';

export type DefinitionValues = {
  goal: string;
  instructions: string;
  expectedOutput: string;
  boundaries: string;
  resources: string;
  skillIds: string[];
};
export const DEFINITION_FIELDS = [
  { key: 'goal', label: 'Goal', hint: 'What should this agent achieve?', max: 2000, rows: 2 },
  {
    key: 'instructions',
    label: 'Instructions',
    hint: 'How the agent should approach the work and make decisions.',
    max: 8000,
    rows: 7,
  },
  {
    key: 'expectedOutput',
    label: 'Expected result',
    hint: 'What a successful run should deliver.',
    max: 2000,
    rows: 3,
  },
  {
    key: 'resources',
    label: 'Knowledge & connections',
    hint: 'Where to look. Each run uses only resources its acting person can access.',
    max: 4000,
    rows: 3,
  },
  {
    key: 'boundaries',
    label: 'Boundaries & actions',
    hint: 'What to do or avoid. Every connected action still needs human approval.',
    max: 2000,
    rows: 3,
  },
] as const;

export function DefinitionFields({
  value,
  onChange,
  disabled = false,
}: {
  value: DefinitionValues;
  onChange: (patch: Partial<DefinitionValues>) => void;
  disabled?: boolean;
}) {
  const copy = useNamespaceCopy(workspaceFeaturesCopy);
  const editor = useNamespaceCopy(editorCopy);
  const labels = {
    goal: copy.goal,
    instructions: copy.instructions,
    expectedOutput: editor.expectedResult,
    resources: editor.resources,
    boundaries: editor.boundaries,
  } as const;
  const hints = {
    goal: editor.goalHint,
    instructions: editor.instructionsHint,
    expectedOutput: editor.expectedResultHint,
    resources: editor.resourcesHint,
    boundaries: editor.boundariesHint,
  } as const;
  return (
    <div className="ws-agent-definition">
      {DEFINITION_FIELDS.map(({ key, label, hint, max, rows }) => (
        <label className="ws-agents-field" key={key}>
          <span>{labels[key]}</span>
          <small>{hints[key]}</small>
          <textarea
            aria-label={labels[key]}
            value={value[key]}
            rows={rows}
            maxLength={max}
            disabled={disabled}
            onChange={(event) => onChange({ [key]: event.target.value })}
          />
        </label>
      ))}
      <OptionalSkills
        value={value.skillIds}
        disabled={disabled}
        onChange={(skillIds) => onChange({ skillIds })}
      />
    </div>
  );
}

export function OptionalSkills({
  value,
  disabled,
  onChange,
  legend,
  note,
}: {
  value: string[];
  disabled: boolean;
  onChange: (ids: string[]) => void;
  legend?: string;
  note?: string;
}) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const editor = useNamespaceCopy(editorCopy);
  const ws = useWorkspace();
  const company = useCompany();
  const library = useSkillLibrary(true, `${ws.user?.id ?? ''}\0${company.data?.company?.id ?? ''}`);
  const skills = library.skills.filter((skill) => skill.publishedRevision !== null);
  const options = [
    ...skills.map((skill) => ({ id: skill.id, name: skill.published?.name ?? skill.draft.name })),
    ...value
      .filter((id) => !skills.some((skill) => skill.id === id))
      .map((id) => ({ id, name: id })),
  ];
  return (
    <fieldset className="ws-agent-methods" disabled={disabled}>
      <legend>{legend ?? editor.skillsOptional}</legend>
      <p className="ws-muted">{note ?? editor.skillsOptionalNote}</p>
      {options.length === 0 && (
        <small>{uiText('No published Skills available. You can add them later.')}</small>
      )}
      {options.map((skill) => (
        <label key={skill.id} className="ws-agents-switch">
          <input
            type="checkbox"
            checked={value.includes(skill.id)}
            onChange={(event) =>
              onChange(
                event.target.checked ? [...value, skill.id] : value.filter((id) => id !== skill.id),
              )
            }
          />
          <span>{skill.name}</span>
        </label>
      ))}
    </fieldset>
  );
}

export function DefinitionPreview({ value }: { value: DefinitionValues }) {
  const copy = useNamespaceCopy(workspaceFeaturesCopy);
  const editor = useNamespaceCopy(editorCopy);
  const labels = {
    goal: copy.goal,
    instructions: copy.instructions,
    expectedOutput: editor.expectedResult,
    resources: editor.resources,
    boundaries: editor.boundaries,
  } as const;
  return (
    <div className="ws-agent-definition">
      {DEFINITION_FIELDS.map(({ key, label }) => (
        <section className="ws-agent-definition-section" key={key}>
          <h4>{labels[key]}</h4>
          <WorkspaceMarkdown text={value[key] || editor.notSpecified} />
        </section>
      ))}
      <OptionalSkills value={value.skillIds} disabled onChange={() => {}} />
    </div>
  );
}

export function definitionValues(args: Record<string, unknown>): DefinitionValues {
  return {
    goal: String(args.goal ?? ''),
    instructions: String(args.instructions ?? ''),
    expectedOutput: String(args.expectedOutput ?? ''),
    boundaries: String(args.boundaries ?? ''),
    resources: String(args.resources ?? ''),
    skillIds: Array.isArray(args.skillIds) ? (args.skillIds as string[]) : [],
  };
}
